import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Building2, TrendingUp, Globe, Users, Search, ChevronDown, ChevronUp, ExternalLink, BarChart3, Shield, Award } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { AICCIMethodologyLink } from "../dashboard/AICCIMethodology";
import { DashboardSkeleton } from "../common/DashboardSkeleton";
import { usePrefetchedData } from "../common/DataPrefetch";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const OC = { care: "#10b981", utility: "#3b82f6", creative: "#a855f7", extraction: "#ef4444" };
const OC_LABELS = { care: "Care", utility: "Utility", creative: "Creative", extraction: "Extraction" };

/* ─── Animated Counter ─── */
const Counter = ({ value, suffix = "" }) => {
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    if (!value) return;
    const target = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(target)) { setDisplay(value); return; }
    const startTime = performance.now();
    const animate = (now) => {
      const progress = Math.min((now - startTime) / 1200, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(progress < 1 ? Math.round(eased * target) : target);
      if (progress < 1) ref.current = requestAnimationFrame(animate);
    };
    ref.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(ref.current);
  }, [value]);
  return <>{typeof display === "number" ? (Number.isInteger(display) ? display.toLocaleString() : display.toFixed(1)) : display}{suffix}</>;
};

/* ─── Scroll Reveal ─── */
const Reveal = ({ children, delay = 0 }) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(20px)", transition: `all 0.6s ease ${delay}ms` }}>
      {children}
    </div>
  );
};

/* ─── Stat Card ─── */
const StatCard = ({ icon: Icon, label, value, suffix, color, sub, delay = 0 }) => (
  <Reveal delay={delay}>
    <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4 hover:border-white/[0.12] transition-all" data-testid={`stat-${label.toLowerCase().replace(/\s/g,'-')}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4" style={{ color }} />
        <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: `${color}90` }}>{label}</span>
      </div>
      <p className="text-2xl font-black text-white"><Counter value={value} suffix={suffix} /></p>
      {sub && <p className="text-[10px] text-slate-500 mt-1">{sub}</p>}
    </div>
  </Reveal>
);

/* ─── Company Row ─── */
const CompanyRow = ({ company, rank, expanded, onToggle, onViewDetail }) => {
  const od = company.orientation_distribution || {};
  return (
    <div className="border-b border-white/[0.04] last:border-b-0">
      <div onClick={onToggle} className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-white/[0.02] transition-colors" data-testid={`company-row-${rank}`}>
        <span className="text-xs font-bold text-slate-500 w-8 text-right">{rank}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-white truncate">{company.company_name}</span>
            <span className="text-[9px] px-2 py-0.5 rounded-full border" style={{
              color: OC[company.dominant_orientation],
              borderColor: `${OC[company.dominant_orientation]}33`,
              background: `${OC[company.dominant_orientation]}11`,
            }}>{OC_LABELS[company.dominant_orientation]}</span>
          </div>
          <p className="text-[10px] text-slate-500">{company.domain} &middot; {company.country} &middot; {company.industry}</p>
        </div>
        <div className="flex items-center gap-4 flex-shrink-0">
          <div className="text-right">
            <p className="text-sm font-black" style={{ color: company.company_cii >= 30 ? "#10b981" : company.company_cii >= 15 ? "#f59e0b" : "#ef4444" }}>
              {company.company_cii}
            </p>
            <p className="text-[9px] text-slate-500">CII</p>
          </div>
          <div className="text-right">
            <p className="text-sm font-bold text-slate-200">{company.product_count}</p>
            <p className="text-[9px] text-slate-500">products</p>
          </div>
          {/* Mini orientation bar */}
          <div className="w-20 h-2 rounded-full overflow-hidden bg-white/[0.04] flex">
            {Object.entries(od).filter(([,v]) => v > 0).map(([k, v]) => (
              <div key={k} style={{ width: `${v}%`, background: OC[k] }} />
            ))}
          </div>
          {expanded ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
        </div>
      </div>
      {expanded && (
        <div className="px-4 pb-4 pt-1 ml-11 border-l-2 border-white/[0.06] space-y-2" data-testid={`company-detail-${rank}`}>
          <div className="flex flex-wrap gap-4 text-xs text-slate-400">
            <span>Software: <strong className="text-emerald-400">{company.software_count || 0}</strong></span>
            <span>Hardware: <strong className="text-blue-400">{company.hardware_count || 0}</strong></span>
            <span>Founder: <strong className="text-slate-200">{(company.founder_composition || "unknown").replace("_"," ")}</strong></span>
            {onViewDetail && <button onClick={() => onViewDetail(company.domain)} className="text-amber-400 hover:text-amber-300 font-medium ml-auto">View full profile →</button>}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {(company.products || []).slice(0, 12).map((p, i) => (
              <span key={i} className="text-[10px] px-2 py-0.5 bg-white/[0.04] border border-white/[0.06] rounded-full text-slate-300">{p}</span>
            ))}
            {(company.products || []).length > 12 && <span className="text-[10px] text-slate-500">+{company.products.length - 12} more</span>}
          </div>
          {company.industries && Object.keys(company.industries).length > 1 && (
            <div className="flex flex-wrap gap-2 mt-1">
              {Object.entries(company.industries).map(([ind, count]) => (
                <span key={ind} className="text-[9px] px-2 py-0.5 bg-violet-500/10 border border-violet-500/15 rounded-full text-violet-300">{ind} ({count})</span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ─── Custom Tooltip ─── */
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0c1222] border border-white/10 rounded-lg px-3 py-2 text-xs shadow-xl">
      <p className="font-bold text-white mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || "#94a3b8" }}>{p.name || "Count"}: {typeof p.value === "number" ? p.value.toLocaleString() : p.value}</p>
      ))}
    </div>
  );
};

/* ═══════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════ */
export const CompanyObservatory = ({ onViewDetail, onCompare }) => {
  const prefetched = usePrefetchedData();
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedIdx, setExpandedIdx] = useState(null);
  const [sortBy, setSortBy] = useState("cii");
  const [filterOrientation, setFilterOrientation] = useState("");
  const [filterIndustry, setFilterIndustry] = useState("");
  const [minProducts, setMinProducts] = useState(2);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [notable, setNotable] = useState([]);

  useEffect(() => {
    axios.get(`${API}/companies/notable`)
      .then(res => setNotable(res.data.companies || []))
      .catch(() => {});
  }, []);

  // Sync from prefetch store
  useEffect(() => {
    if (prefetched.companyStats && !stats) setStats(prefetched.companyStats);
    if (prefetched.companyLeaderboard && companies.length === 0) {
      setCompanies(prefetched.companyLeaderboard.companies || []);
    }
    if ((prefetched.companyStats || stats) && (prefetched.companyLeaderboard || companies.length > 0)) {
      setLoading(false);
    }
  });

  const initialMount = useRef(true);

  useEffect(() => {
    const isInitial = initialMount.current;
    initialMount.current = false;

    // On initial mount with default filters, let prefetch handle it
    if (isInitial && sortBy === "cii" && !filterOrientation && !filterIndustry && minProducts === 2) {
      // Delayed fallback if prefetch hasn't delivered
      const timer = setTimeout(() => {
        if (stats && companies.length > 0) return;
        axios.all([
          stats ? null : axios.get(`${API}/companies/stats`),
          axios.get(`${API}/companies/leaderboard`, { params: { sort_by: sortBy, min_products: minProducts, limit: 50 } }),
        ].filter(Boolean)).then(results => {
          const [sRes, lRes] = stats ? [null, results[0]] : results;
          if (sRes) setStats(prev => prev || sRes.data);
          if (lRes) setCompanies(lRes.data.companies || []);
          setLoading(false);
        }).catch(() => setLoading(false));
      }, 1500);
      return () => clearTimeout(timer);
    }

    // Filter change — always fetch fresh
    setLoading(true);
    Promise.all([
      stats ? Promise.resolve({ data: stats }) : axios.get(`${API}/companies/stats`),
      axios.get(`${API}/companies/leaderboard`, {
        params: { sort_by: sortBy, orientation: filterOrientation || undefined, industry: filterIndustry || undefined, min_products: minProducts, limit: 50 }
      }),
    ]).then(([sRes, lRes]) => {
      setStats(prev => prev || sRes.data);
      setCompanies(lRes.data.companies || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [sortBy, filterOrientation, filterIndustry, minProducts]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = async (q) => {
    setSearchQuery(q);
    if (q.length < 2) { setSearchResults(null); return; }
    try {
      const res = await axios.get(`${API}/companies/search`, { params: { q } });
      setSearchResults(res.data.results || []);
    } catch { setSearchResults(null); }
  };

  if (loading && !stats) {
    return <DashboardSkeleton galaxyColor="amber" />;
  }

  const orientPieData = stats?.orientations ? Object.entries(stats.orientations).map(([k, v]) => ({ name: OC_LABELS[k] || k, value: v, fill: OC[k] || "#475569" })) : [];
  const INDUSTRY_SHORT = {
    "Entertainment & Gaming": "Entertainment",
    "Developer Tools & Infrastructure": "Dev Tools",
    "General Productivity": "Productivity",
    "Creative & Media": "Creative",
    "Education & Research": "Education",
    "Finance & Insurance": "Finance",
    "Healthcare & Life Sciences": "Healthcare",
    "Retail & E-commerce": "Retail",
    "Legal & Compliance": "Legal",
    "Security & Defense": "Security",
    "Social Impact & Nonprofit": "Social Impact",
    "Social Impact & Nonprofits": "Social Impact",
    "Agriculture & Environment": "Agriculture",
    "Manufacturing & Robotics": "Manufacturing",
    "Transportation & Logistics": "Transport",
    "Communication & Telecom": "Telecom",
  };
  const industryData = (stats?.industries || []).slice(0, 10).map(d => ({
    ...d,
    label: INDUSTRY_SHORT[d.industry] || (d.industry?.length > 14 ? d.industry.substring(0, 12) + ".." : d.industry)
  }));
  const countryData = (stats?.countries || []).slice(0, 12);
  const ciiDistData = stats?.cii_distribution || [];

  return (
    <div className="space-y-8 animate-fade-in pb-8" data-testid="company-observatory">
      {/* Hero */}
      <Reveal>
        <div className="relative rounded-2xl overflow-hidden" style={{ background: "linear-gradient(135deg, #0a0f1a 0%, #1a1015 50%, #0f0a1a 100%)" }}>
          <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at 30% 50%, rgba(245,158,11,0.06), transparent 60%)" }} />
          <div className="relative px-8 py-10 text-center">
            <p className="text-[10px] font-bold text-amber-400 uppercase tracking-[0.3em] mb-2">Galaxy 3 &middot; Companies Observatory</p>
            <h1 className="text-3xl sm:text-4xl font-black text-white mb-3">The Consciousness of AI Companies</h1>
            <p className="text-sm text-slate-400 max-w-2xl mx-auto leading-relaxed">
              Tracking <span className="text-amber-400 font-bold">{(stats?.total || 0).toLocaleString()}</span> AI companies, linked to {(stats?.total_products_tracked || 0).toLocaleString()} tracked products.
              <span className="text-slate-500 ml-1">Which companies build with care? Which optimize for extraction?</span>
            </p>
            <div className="mt-6 inline-flex items-center gap-3 px-6 py-3 bg-amber-500/10 border border-amber-500/20 rounded-full">
              <span className="text-[9px] font-bold text-amber-400/60 uppercase tracking-widest">Company CII</span>
              <span className="text-3xl font-black text-amber-400">{stats?.avg_cii || 0}</span>
              <span className="text-xs text-slate-400">/ 100</span>
              <AICCIMethodologyLink />
            </div>
          </div>
        </div>
      </Reveal>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <StatCard icon={Building2} label="Total Companies" value={stats?.total} color="#f59e0b" delay={0} />
        <StatCard icon={Award} label="Avg CII" value={stats?.avg_cii} color="#10b981" delay={100} />
        <StatCard icon={TrendingUp} label="Multi-Product" value={stats?.multi_product_companies} color="#a855f7" sub="Companies with 2+ products" delay={200} />
        <StatCard icon={Shield} label="Care-Oriented" value={stats?.orientations?.care} color="#10b981" sub={`${((stats?.orientations?.care || 0) / (stats?.total || 1) * 100).toFixed(1)}% of all`} delay={300} />
        <StatCard icon={Globe} label="Countries" value={stats?.countries_count || stats?.countries?.length} color="#22d3ee" delay={400} />
      </div>

      {/* Charts Row */}
      <Reveal delay={100}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Orientation Distribution */}
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5" data-testid="orient-chart">
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Value Orientation Distribution</h3>
            <div className="h-52">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={orientPieData} dataKey="value" cx="50%" cy="50%" innerRadius={50} outerRadius={80} strokeWidth={0} paddingAngle={2}>
                    {orientPieData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-4 mt-2">
              {orientPieData.map(d => (
                <span key={d.name} className="flex items-center gap-1.5 text-[10px] text-slate-400">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: d.fill }} />{d.name}: {d.value.toLocaleString()}
                </span>
              ))}
            </div>
          </div>

          {/* CII Distribution Histogram */}
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5" data-testid="cii-dist-chart">
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">CII Score Distribution</h3>
            <div className="h-52">
              <ResponsiveContainer>
                <BarChart data={ciiDistData}>
                  <XAxis dataKey="range" tick={{ fill: "#64748b", fontSize: 10 }} />
                  <YAxis tick={{ fill: "#64748b", fontSize: 10 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="Companies" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </Reveal>

      {/* Industry Breakdown */}
      <Reveal delay={150}>
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5" data-testid="industry-chart">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Industry Breakdown</h3>
          <div className="h-80">
            <ResponsiveContainer>
              <BarChart data={industryData} layout="vertical" margin={{ left: 10, right: 10, top: 5, bottom: 5 }}>
                <XAxis type="number" tick={{ fill: "#64748b", fontSize: 10 }} />
                <YAxis type="category" dataKey="label" tick={{ fill: "#94a3b8", fontSize: 10 }} width={100} interval={0} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" name="Companies" fill="#a855f7" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </Reveal>

      {/* Country Distribution */}
      <Reveal delay={200}>
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5" data-testid="country-chart">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Geographic Distribution</h3>
          <div className="h-72">
            <ResponsiveContainer>
              <BarChart data={countryData} margin={{ bottom: 50, left: 10, right: 10 }}>
                <XAxis dataKey="country" tick={{ fill: "#94a3b8", fontSize: 9, angle: -40, textAnchor: "end" }} interval={0} height={60} />
                <YAxis tick={{ fill: "#64748b", fontSize: 10 }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" name="Companies" fill="#22d3ee" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </Reveal>

      {/* Leaderboard */}
      <Reveal delay={250}>
        {/* Major AI labs spotlight — these get buried thousands of rows deep in the CII ranking */}
        {notable.length > 0 && (
          <div className="mb-6" data-testid="notable-companies-strip">
            <div className="flex items-center gap-2 mb-3">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Major AI Labs & Big Tech</h3>
              <span className="text-[9px] text-slate-600">ranked by CII — tap for full profile</span>
            </div>
            <div className="flex gap-2.5 overflow-x-auto pb-2 -mx-1 px-1">
              {notable.map(c => {
                const cii = c.cii_score;
                const color = cii >= 60 ? "#34d399" : cii >= 40 ? "#fbbf24" : "#f87171";
                return (
                  <button key={c.domain || c.company_name} onClick={() => onViewDetail?.(c.domain)}
                    data-testid={`notable-${(c.company_name || "").toLowerCase().replace(/\s+/g, "-")}`}
                    className="flex-shrink-0 w-36 text-left bg-white/[0.03] border border-white/[0.08] rounded-xl px-3.5 py-3 hover:border-amber-400/40 hover:bg-white/[0.05] transition-all">
                    <p className="text-xs font-bold text-white truncate">{c.company_name}</p>
                    <div className="flex items-baseline gap-1 mt-1.5">
                      <span className="text-xl font-black" style={{ color }}>{cii != null ? Number(cii).toFixed(1) : "—"}</span>
                      <span className="text-[9px] text-slate-500">CII</span>
                    </div>
                    <p className="text-[9px] text-slate-500 mt-0.5">{c.product_count || 0} products · {c.dominant_orientation || c.value_orientation || "—"}</p>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl overflow-hidden" data-testid="company-leaderboard">
          <div className="px-5 py-4 border-b border-white/[0.06] flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex-1">
              <h3 className="text-sm font-bold text-white">Company Leaderboard</h3>
              <p className="text-[10px] text-slate-500">Ranked by Conscious Innovation Index</p>
            </div>
            {/* Search */}
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => handleSearch(e.target.value)}
                placeholder="Search companies..."
                className="pl-8 pr-3 py-1.5 bg-white/[0.04] border border-white/[0.08] rounded-lg text-xs text-white placeholder:text-slate-500 focus:outline-none focus:border-amber-500/30 w-48"
                data-testid="company-search-input"
              />
              {searchResults && (
                <div className="absolute top-full mt-1 left-0 w-72 bg-[#0c1222] border border-white/10 rounded-xl shadow-2xl z-50 max-h-64 overflow-y-auto" data-testid="search-results">
                  {searchResults.length === 0 ? (
                    <p className="text-xs text-slate-500 p-3">No results</p>
                  ) : searchResults.map((c, i) => (
                    <div key={i} className="px-3 py-2 hover:bg-white/[0.04] cursor-pointer border-b border-white/[0.04] last:border-b-0"
                      onClick={() => { setSearchQuery(""); setSearchResults(null); onViewDetail?.(c.domain); }}>
                      <p className="text-xs font-bold text-white">{c.company_name}</p>
                      <p className="text-[10px] text-slate-500">{c.domain} &middot; CII: {c.cii_score ?? "—"} &middot; {c.product_count} products</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Filters */}
            <div className="flex gap-2 flex-wrap">
              <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                className="text-[10px] px-2 py-1 bg-white/[0.04] border border-white/[0.08] rounded-lg text-slate-300 focus:outline-none" data-testid="sort-select">
                <option value="cii">Sort: CII</option>
                <option value="products">Sort: Products</option>
                <option value="name">Sort: Name</option>
              </select>
              <select value={filterOrientation} onChange={e => setFilterOrientation(e.target.value)}
                className="text-[10px] px-2 py-1 bg-white/[0.04] border border-white/[0.08] rounded-lg text-slate-300 focus:outline-none" data-testid="orient-filter">
                <option value="">All Orientations</option>
                <option value="care">Care</option>
                <option value="utility">Utility</option>
                <option value="creative">Creative</option>
                <option value="extraction">Extraction</option>
              </select>
              <select value={minProducts} onChange={e => setMinProducts(Number(e.target.value))}
                className="text-[10px] px-2 py-1 bg-white/[0.04] border border-white/[0.08] rounded-lg text-slate-300 focus:outline-none" data-testid="products-filter">
                <option value={1}>1+ products</option>
                <option value={2}>2+ products</option>
                <option value={3}>3+ products</option>
                <option value={5}>5+ products</option>
              </select>
            </div>
          </div>
          {/* Table */}
          <div className="divide-y divide-white/[0.02]">
            {companies.length === 0 && <p className="text-center text-sm text-slate-500 py-8">No companies match your filters</p>}
            {companies.map((c, i) => (
              <CompanyRow key={c.domain} company={c} rank={i + 1} expanded={expandedIdx === i} onToggle={() => setExpandedIdx(expandedIdx === i ? null : i)} onViewDetail={onViewDetail} />
            ))}
          </div>
        </div>
      </Reveal>
    </div>
  );
};

export default CompanyObservatory;
